import { faTruckBolt } from "@jewlr/pro-light-svg-icons/faTruckBolt"
import { faArrowRightLong } from "@jewlr/pro-regular-svg-icons/faArrowRightLong"
import { faCircle } from "@jewlr/pro-solid-svg-icons/faCircle"
import { faDiamond } from "@jewlr/sharp-solid-svg-icons/faDiamond"
import { faSquare } from "@jewlr/sharp-solid-svg-icons/faSquare"
import { motion } from "framer-motion"
import PropTypes from "prop-types"
import { useEffect, useMemo, useState } from "react"
// import { useSelector } from "react-redux"
import { Link, useLocation } from "react-router-dom"

import { usePopupTrigger } from "areas/footer/jewlr/footer-subscribe"
import { openModal } from "components/modal"
import Button from "components/ui/button"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import Media from "components/ui/media"
import { jewlr_t, isTestMode, IS_GE_CHECKOUT } from "helpers/application"
import { cn } from "styles/utils"

import Clock from "./clock"
import Markdown from "./markdown"

const textVariants = {
  hidden: {
    opacity: 0,
    transitionEnd: {
      display: "none",
    },
  },
  visible: {
    display: "block",
    opacity: 1,
    transition: { delay: 0.5 },
  },
}

const TextRoller = ({ currentIndex, id, isSecondMicroBanner, texts }) => {
  const triggerSignUp = usePopupTrigger()

  return texts.map((text, i) => {
    const triggerPopup = useMemo(() => {
      let trigger = null

      switch (text.type) {
        case "sign-up":
        case "sign-up-countdown":
          trigger = () => {
            if (!isTestMode) {
              dataLayer.push({ event: "trigger-micro-banner-sign-up-unit" })
            }
            triggerSignUp()
          }
          break
        case "modal":
        case "modal-countdown":
          trigger = () => openModal(text.url)
          break
      }

      return trigger
    }, [openModal, triggerSignUp, text.type, text.url])

    const Comp = text.url
      ? text.rich_text
        ? "div"
        : ["modal", "modal-countdown"].includes(text.type)
          ? Button
          : Link
      : "div"
    const compProps = text.url
      ? ["modal", "modal-countdown"].includes(text.type)
        ? { onClick: () => triggerPopup(), text: true }
        : { to: text.url }
      : null

    return (
      <motion.div
        animate={currentIndex === i ? "visible" : "hidden"}
        initial="hidden"
        key={`microbanner-${i}-${id}`}
        variants={textVariants}
      >
        {["countdown", "sign-up-countdown", "modal-countdown"].includes(
          text.type
        ) ? (
          <Clock
            Comp={Comp}
            compProps={compProps}
            isSecondMicroBanner={isSecondMicroBanner}
            text={text}
            triggerPopup={triggerPopup}
          />
        ) : text.type === "delivery" ? (
          <Comp
            className="flex items-center justify-center"
            data-lc="countdown-banner"
            {...compProps}
          >
            <FontAwesomeIcon className="text-20 mr-8" icon={faTruckBolt} />
            {text.rich_text ? (
              <Markdown
                classNames={text.style?.classNames}
                defaultClassName="large:text-15 text-14 text-left"
                triggerPopup={triggerPopup}
                type={text.type}
              >
                {text.rich_text}
              </Markdown>
            ) : (
              <span
                className={cn(
                  "large:text-15 text-14 text-left",
                  text.style?.className
                )}
                dangerouslySetInnerHTML={{ __html: text.text }}
              />
            )}
          </Comp>
        ) : (
          <Comp
            className="flex items-center"
            data-lc="countdown-banner"
            {...compProps}
          >
            {id === "desktop" && !["sign-up", "modal"].includes(text.type) && (
              <FontAwesomeIcon className="text-12 mr-6" icon={faDiamond} />
            )}
            {text.rich_text ? (
              <Markdown
                classNames={text.style?.classNames}
                defaultClassName="large:text-15 text-14 text-left"
                triggerPopup={triggerPopup}
                type={text.type}
              >
                {text.rich_text}
              </Markdown>
            ) : (
              <span
                className={cn("large:text-15 text-14", text.style?.className)}
                dangerouslySetInnerHTML={{ __html: text.text }}
              />
            )}
            {id === "desktop" && !["sign-up", "modal"].includes(text.type) && (
              <FontAwesomeIcon
                className="text-12 ml-6"
                icon={faArrowRightLong}
              />
            )}
          </Comp>
        )}
      </motion.div>
    )
  })
}

const CountdownBanner = ({
  microbanner,
  isSecondMicroBanner,
  showOnDesktop = true,
  showOnMobile = true,
}) => {
  const [currentMobileIndex, setCurrentMobileIndex] = useState(0)
  const [currentDesktopIndex, setCurrentDesktopIndex] = useState(0)
  const is_ge_checkout = IS_GE_CHECKOUT()

  let desktopTexts = microbanner.desktop_texts
  let mobileTexts = microbanner.mobile_texts
  // let desktopTexts = microbanner.desktop_texts.filter(({ type }) =>
  //   isSecondMicroBanner && microbannerExp === 1
  //     ? ["sign-up", "sign-up-countdown"].includes(type)
  //     : !["sign-up", "sign-up-countdown"].includes(type)
  // )
  // let mobileTexts = microbanner.mobile_texts.filter(({ type }) =>
  //   isSecondMicroBanner && microbannerExp === 1
  //     ? ["sign-up", "sign-up-countdown"].includes(type)
  //     : !["sign-up", "sign-up-countdown"].includes(type)
  // )
  const { className, ...style } = microbanner.style || {}

  const location = useLocation()
  const isProductPage = location.pathname.includes("/products/")

  const handleClickForSimplifyAboveFoldExp = () => {
    if (!isTestMode && isProductPage && isSecondMicroBanner) {
      dataLayer.push({
        event: "clicked-countdown-promotion-pdp",
      })
    }
  }

  useEffect(() => {
    if (mobileTexts.length > 0) {
      const mobileInterval = setInterval(() => {
        setCurrentMobileIndex(
          (currentIndex) => (currentIndex + 1) % mobileTexts.length
        )
      }, 5000)
      return () => clearInterval(mobileInterval)
    }
  }, [mobileTexts])

  useEffect(() => {
    if (desktopTexts.length > 0) {
      const desktopInterval = setInterval(() => {
        setCurrentDesktopIndex(
          (currentIndex) => (currentIndex + 1) % desktopTexts.length
        )
      }, 5000)
      return () => clearInterval(desktopInterval)
    }
  }, [desktopTexts])

  return (
    <>
      {mobileTexts?.length > 0 && showOnMobile && (
        <Media
          className={cn(
            "bg-black-print w-full px-16 text-white",
            isSecondMicroBanner
              ? "h-(--second-microbanner-height)"
              : "h-(--countdown-banner-height)",
            className
          )}
          lessThan={isSecondMicroBanner ? "desktop" : "large"}
          {...style}
        >
          <div
            className={cn(
              "large:px-16 flex h-full w-full items-center justify-center text-center",
              !isSecondMicroBanner && "large:justify-between"
            )}
            onClick={handleClickForSimplifyAboveFoldExp}
          >
            <TextRoller
              currentIndex={currentMobileIndex}
              id="mobile"
              isSecondMicroBanner={isSecondMicroBanner}
              texts={mobileTexts}
            />
          </div>
        </Media>
      )}
      {(!isSecondMicroBanner || desktopTexts?.length > 0) && showOnDesktop && (
        <Media
          className={cn(
            "bg-black-print w-full text-white",
            isSecondMicroBanner
              ? "h-(--second-microbanner-height)"
              : "h-(--countdown-banner-height)",
            className
          )}
          greaterThanOrEqual={isSecondMicroBanner ? "desktop" : "large"}
          {...style}
        >
          <div
            className={cn(
              "large:px-16 flex h-full w-full items-center justify-center text-center",
              !isSecondMicroBanner && "large:justify-between"
            )}
          >
            {isSecondMicroBanner ? (
              <TextRoller
                currentIndex={currentDesktopIndex}
                id="desktop"
                isSecondMicroBanner
                texts={desktopTexts}
              />
            ) : (
              <>
                <ul
                  className={cn(
                    "text-15 inline-flex list-none items-center font-normal",
                    "[&_li]:inline-flex [&_li]:items-center [&_li]:px-6"
                  )}
                >
                  <li>
                    <FontAwesomeIcon
                      className="text-12 mr-6"
                      icon={faDiamond}
                    />
                    <Link to="/info/shipping_information">
                      FREE {jewlr_t("terms.Shipping")}
                    </Link>
                  </li>
                  <li>
                    <FontAwesomeIcon className="text-10 mr-6" icon={faCircle} />
                    <Link to="/info/returns">
                      {is_ge_checkout ? "30" : "99"}-Day Returns
                    </Link>
                  </li>
                  <li>
                    <FontAwesomeIcon className="text-12 mr-6" icon={faSquare} />
                    <Link to="/info/warranty-information">
                      One-Year Warranty
                    </Link>
                  </li>
                </ul>
                {desktopTexts.length > 0 && (
                  <div>
                    <TextRoller
                      currentIndex={currentDesktopIndex}
                      id="desktop"
                      isSecondMicroBanner
                      texts={desktopTexts}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </Media>
      )}
    </>
  )
}

CountdownBanner.propTypes = {
  isSecondMicroBanner: PropTypes.bool,
  microbanner: PropTypes.object,
  showOnDesktop: PropTypes.bool,
  showOnMobile: PropTypes.bool,
}

export default CountdownBanner
